/**
 * OnboardingCustomizationPage
 *
 * Restricted-state supervisor editor for their tenant's onboarding
 * flow. Reads / writes state_onboarding_config via:
 *   GET  /api/admin/state-onboarding/:code
 *   PUT  /api/admin/state-onboarding/:code
 *
 * Field rules (per user spec):
 *   step1  — optional (toggle) + upload your own intro video + title
 *   step2a — manual list of interview questions
 *   step2b — optional
 *   step2c — optional + NDA upload box when enabled
 *   step2d — optional
 *   step2e — optional
 *   step2f — optional
 *   step2g — OMITTED entirely (server won't accept the key)
 *   step2h — optional + upload your own video + title
 *   step2i — optional
 *   step2j — optional
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useStateScope } from '../context/StateScopeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { ArrowLeft, Plus, Upload, X, ClipboardList, Video, FileText, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * readAsDataURL — shared tiny promise wrapper; every upload path in
 * this page (video, NDA) turns a File into a base64 data: URI that
 * we persist to state_onboarding_config.
 */
const readAsDataURL = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(r.result);
  r.onerror = reject;
  r.readAsDataURL(file);
});

/**
 * Row — collapsible step card. Each step shows its enabled toggle
 * on the header row; body content is only rendered when enabled and
 * typically carries per-step extras (video upload, questions, NDA).
 */
const StepRow = ({ id, title, required, enabled, onToggle, children, testid }) => (
  <Card className="rounded-2xl" data-testid={testid || `onb-row-${id}`}>
    <CardHeader className="pb-2 flex flex-row items-start justify-between gap-3">
      <div>
        <CardTitle className="text-sm flex items-center gap-2">
          <span className="text-[11px] font-bold tracking-wider text-[#586D52] uppercase">{id}</span>
          <span>{title}</span>
          {required && <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 dark:bg-[#2a2a2a]">Required</span>}
        </CardTitle>
      </div>
      {!required && (
        <Switch
          checked={!!enabled}
          onCheckedChange={onToggle}
          data-testid={`onb-toggle-${id}`}
        />
      )}
    </CardHeader>
    {enabled && children && (
      <CardContent className="pt-0 space-y-2">
        {children}
      </CardContent>
    )}
  </Card>
);

const VideoUpload = ({ url, title, onChange, idPrefix }) => {
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 25 * 1024 * 1024) {
      toast.error('Video must be smaller than 25 MB');
      return;
    }
    const data = await readAsDataURL(f);
    onChange({ video_url: data, title: title || f.name.replace(/\.[^.]+$/, '') });
  };
  return (
    <div className="space-y-2">
      <div className="space-y-1">
        <Label className="text-[11px]">Video title</Label>
        <Input
          value={title || ''}
          onChange={(e) => onChange({ title: e.target.value })}
          placeholder="e.g. Welcome from your supervisor"
          className="h-9 text-sm"
          data-testid={`${idPrefix}-title`}
        />
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        <Label
          htmlFor={`${idPrefix}-file`}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold bg-[#586D52] text-white cursor-pointer hover:bg-[#4a5d46]"
          data-testid={`${idPrefix}-upload-label`}
        >
          <Upload className="w-3.5 h-3.5" /> {url ? 'Replace video' : 'Upload video'}
        </Label>
        <input
          id={`${idPrefix}-file`}
          type="file"
          accept="video/*"
          onChange={onFile}
          className="hidden"
          data-testid={`${idPrefix}-file`}
        />
        {url && (
          <>
            <span className="text-[11px] text-muted-foreground">Video attached ✓</span>
            <Button size="sm" variant="ghost" onClick={() => onChange({ video_url: null })} data-testid={`${idPrefix}-clear`}>
              <X className="w-3.5 h-3.5" />
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

const FileUpload = ({ url, onChange, idPrefix, accept = 'application/pdf', label = 'Upload file' }) => {
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) {
      toast.error('File must be smaller than 5 MB');
      return;
    }
    const data = await readAsDataURL(f);
    onChange(data);
  };
  return (
    <div className="flex flex-wrap gap-2 items-center">
      <Label
        htmlFor={`${idPrefix}-file`}
        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold bg-[#586D52] text-white cursor-pointer hover:bg-[#4a5d46]"
        data-testid={`${idPrefix}-upload-label`}
      >
        <Upload className="w-3.5 h-3.5" /> {url ? 'Replace' : label}
      </Label>
      <input
        id={`${idPrefix}-file`}
        type="file"
        accept={accept}
        onChange={onFile}
        className="hidden"
        data-testid={`${idPrefix}-file`}
      />
      {url && (
        <>
          <span className="text-[11px] text-muted-foreground">Attached ✓</span>
          <Button size="sm" variant="ghost" onClick={() => onChange(null)} data-testid={`${idPrefix}-clear`}>
            <X className="w-3.5 h-3.5" />
          </Button>
        </>
      )}
    </div>
  );
};

/** Step 2a — manual interview question list editor. */
const QuestionsEditor = ({ questions, onChange }) => {
  const [draft, setDraft] = useState('');
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    onChange([...(questions || []), v]);
    setDraft('');
  };
  const remove = (i) => {
    const next = [...(questions || [])];
    next.splice(i, 1);
    onChange(next);
  };
  return (
    <div className="space-y-2">
      <Label className="text-[11px]">Interview questions</Label>
      {(questions || []).length === 0 && (
        <p className="text-[11px] text-muted-foreground">No questions yet.</p>
      )}
      <ul className="space-y-1" data-testid="onb-questions-list">
        {(questions || []).map((q, i) => (
          <li key={i} className="flex items-center gap-2 bg-gray-50 dark:bg-[#0d0d0d] rounded px-2 py-1">
            <span className="text-[11px] w-5 text-muted-foreground">{i + 1}.</span>
            <span className="text-sm flex-1">{q}</span>
            <Button size="icon" variant="ghost" onClick={() => remove(i)} data-testid={`onb-q-remove-${i}`}>
              <X className="w-3.5 h-3.5" />
            </Button>
          </li>
        ))}
      </ul>
      <div className="flex gap-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder="Add a question and press Enter"
          className="h-9 text-sm"
          data-testid="onb-q-input"
        />
        <Button size="sm" onClick={add} className="bg-[#586D52] hover:bg-[#4a5d46]" data-testid="onb-q-add">
          <Plus className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

export default function OnboardingCustomizationPage() {
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const { isRestricted, stateCode, displayName } = useStateScope();
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const isAllowed = isRestricted && user?.role === 'supervisor';

  useEffect(() => {
    if (!isAllowed || !stateCode) { setLoading(false); return; }
    (async () => {
      try {
        const r = await api.get(`/admin/state-onboarding/${encodeURIComponent(stateCode)}`);
        setCfg(r.data?.config || null);
      } catch (e) {
        toast.error('Could not load onboarding config');
      } finally {
        setLoading(false);
      }
    })();
  }, [api, isAllowed, stateCode]);

  const patch = (key, value) => {
    setCfg((c) => ({ ...(c || {}), [key]: { ...(c?.[key] || {}), ...value } }));
  };

  const save = async () => {
    if (!stateCode || !cfg) return;
    setSaving(true);
    try {
      const r = await api.put(
        `/admin/state-onboarding/${encodeURIComponent(stateCode)}`,
        { config: cfg },
      );
      setCfg(r.data?.config || cfg);
      toast.success('Onboarding saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  if (!isAllowed) {
    return (
      <div className="min-h-screen p-6 flex flex-col items-center justify-center text-center">
        <p className="text-lg font-semibold">Restricted-state supervisors only</p>
        <Button className="mt-4" variant="outline" onClick={() => navigate('/')}>Back home</Button>
      </div>
    );
  }
  if (loading || !cfg) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000] pb-24"
      data-testid="onboarding-customization-page"
    >
      <div className="max-w-3xl mx-auto p-3">
        <div className="flex items-center gap-2 mb-3">
          <Button size="icon" variant="ghost" onClick={() => navigate('/customization')} data-testid="onb-back">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ClipboardList className="w-6 h-6 text-[#586D52]" />
            Onboarding — {displayName || stateCode}
          </h1>
        </div>
        <p className="text-xs text-muted-foreground mb-4">
          Turn steps on or off for new hires in your state, upload your own intro videos, add custom interview questions, and attach an NDA. Step 2g is not available on restricted-state onboarding.
        </p>

        <div className="space-y-3">
          <StepRow
            id="step1"
            title="Intro video — welcome message"
            enabled={cfg.step1.enabled}
            onToggle={(v) => patch('step1', { enabled: v })}
          >
            <VideoUpload
              url={cfg.step1.video_url}
              title={cfg.step1.title}
              onChange={(p) => patch('step1', p)}
              idPrefix="onb-step1"
            />
            <p className="text-[11px] text-muted-foreground flex items-center gap-1">
              <Video className="w-3 h-3" /> The default ORB intro video is removed for restricted states — upload your own.
            </p>
          </StepRow>

          <StepRow
            id="step2a"
            title="Application — interview questions"
            enabled={cfg.step2a.enabled}
            onToggle={(v) => patch('step2a', { enabled: v })}
          >
            <QuestionsEditor
              questions={cfg.step2a.questions || []}
              onChange={(qs) => patch('step2a', { questions: qs })}
            />
          </StepRow>

          <StepRow id="step2b" title="Identity verification" enabled={cfg.step2b.enabled} onToggle={(v) => patch('step2b', { enabled: v })} />

          <StepRow
            id="step2c"
            title="NDA"
            enabled={cfg.step2c.enabled}
            onToggle={(v) => patch('step2c', { enabled: v })}
          >
            <div className="space-y-1">
              <Label className="text-[11px]">Upload the NDA new hires will sign</Label>
              <FileUpload
                url={cfg.step2c.nda_url}
                onChange={(u) => patch('step2c', { nda_url: u })}
                idPrefix="onb-step2c"
                accept="application/pdf"
                label="Upload NDA (PDF)"
              />
              <p className="text-[11px] text-muted-foreground flex items-center gap-1">
                <FileText className="w-3 h-3" /> PDF, max 5 MB.
              </p>
            </div>
          </StepRow>

          <StepRow id="step2d" title="MFA activation" enabled={cfg.step2d.enabled} onToggle={(v) => patch('step2d', { enabled: v })} />
          <StepRow id="step2e" title="Leadership interview" enabled={cfg.step2e.enabled} onToggle={(v) => patch('step2e', { enabled: v })} />
          <StepRow id="step2f" title="Peer interview" enabled={cfg.step2f.enabled} onToggle={(v) => patch('step2f', { enabled: v })} />

          {/* step2g is intentionally omitted from restricted onboarding */}

          <StepRow
            id="step2h"
            title="How ORB works — overview video"
            enabled={cfg.step2h.enabled}
            onToggle={(v) => patch('step2h', { enabled: v })}
          >
            <VideoUpload
              url={cfg.step2h.video_url}
              title={cfg.step2h.title}
              onChange={(p) => patch('step2h', p)}
              idPrefix="onb-step2h"
            />
            <p className="text-[11px] text-muted-foreground flex items-center gap-1">
              <Video className="w-3 h-3" /> The default ORB overview video is replaced with your own recording for this state.
            </p>
          </StepRow>

          <StepRow
            id="step2i"
            title="Role duties walkthrough"
            enabled={cfg.step2i.enabled}
            onToggle={(v) => patch('step2i', { enabled: v })}
          >
            <VideoUpload
              url={cfg.step2i.video_url}
              title={cfg.step2i.title}
              onChange={(p) => patch('step2i', p)}
              idPrefix="onb-step2i"
            />
            <p className="text-[11px] text-muted-foreground flex items-center gap-1">
              <Video className="w-3 h-3" /> Upload your role-duties walkthrough video. New hires will watch this on Step 2i.
            </p>
          </StepRow>
          <StepRow id="step2j" title="Live Q&A session" enabled={cfg.step2j.enabled} onToggle={(v) => patch('step2j', { enabled: v })} />

          <div className="sticky bottom-4 z-10 flex justify-end pt-2">
            <Button
              onClick={save}
              disabled={saving}
              className="bg-[#586D52] hover:bg-[#4a5d46] shadow-lg"
              data-testid="onb-save-btn"
            >
              {saving ? 'Saving…' : 'Save Onboarding'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
